import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X, Navigation } from 'lucide-react';
import { DAY_COLORS } from './plannerHelpers';
import useRoutes from './useRoutes';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const TILE_URL = MAPBOX_TOKEN
  ? `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
  : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR = MAPBOX_TOKEN ? '&copy; Mapbox' : '&copy; OpenStreetMap contributors';

function FitBoundsController({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 1) {
      map.fitBounds(L.latLngBounds(positions), { padding: [48, 48], maxZoom: 13 });
    } else if (positions.length === 1) {
      map.setView(positions[0], 13);
    }
  }, [positions, map]);
  return null;
}

export default function FullScreenMapModal({ activities, days, itinerary, onClose }) {
  const [selectedDay, setSelectedDay] = useState(null); // null = all days
  const [dayLocationLabels, setDayLocationLabels] = useState({});

  const activitiesWithCoords = useMemo(
    () => activities.filter(a => a.latitude && a.longitude && (Math.abs(a.latitude) > 0.1 || Math.abs(a.longitude) > 0.1)),
    [activities]
  );

  // Build day → city label map
  useEffect(() => {
    const labels = {};
    activities.forEach(a => {
      if (a.city_name && !labels[a.day_number]) {
        labels[a.day_number] = a.city_name;
      }
    });
    setDayLocationLabels(labels);
  }, [activities]);

  const filteredActivities = useMemo(
    () => selectedDay ? activitiesWithCoords.filter(a => a.day_number === selectedDay) : activitiesWithCoords,
    [activitiesWithCoords, selectedDay]
  );

  const routes = useRoutes(filteredActivities);

  const fitPositions = useMemo(
    () => filteredActivities.map(a => [a.latitude, a.longitude]),
    [filteredActivities]
  );

  // Activity markers — numbered within each day
  const activityMarkers = useMemo(() => {
    const dayPositionCounters = {};
    return filteredActivities
      .sort((a, b) => a.day_number !== b.day_number ? a.day_number - b.day_number : (a.position || 0) - (b.position || 0))
      .map(a => {
        dayPositionCounters[a.day_number] = (dayPositionCounters[a.day_number] || 0) + 1;
        return { ...a, positionInDay: dayPositionCounters[a.day_number] };
      });
  }, [filteredActivities]);

  const handleDayFilter = useCallback((day) => {
    setSelectedDay(prev => prev === day ? null : day);
  }, []);

  const openGoogleMaps = useCallback(() => {
    const pts = filteredActivities.filter(a => a.latitude && a.longitude);
    if (pts.length === 0) return;
    const waypoints = pts.map(a => `${a.latitude},${a.longitude}`).join('/');
    window.open(`https://www.google.com/maps/dir/${waypoints}`, '_blank');
  }, [filteredActivities]);

  if (activitiesWithCoords.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4" onClick={e => e.stopPropagation()}>
          <p className="text-charcoal-500 font-semibold mb-2">No map data yet</p>
          <p className="text-platinum-500 text-sm mb-4">Activities need GPS coordinates to appear on the map. Generate your itinerary first.</p>
          <button onClick={onClose} className="px-4 py-2 bg-coral-400 text-white rounded-lg font-medium">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-charcoal-500" onClick={onClose}>
      {/* Map container */}
      <div className="flex-1 relative" onClick={e => e.stopPropagation()}>
        <MapContainer
          center={fitPositions[0] || [0, 0]}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
          dragging={true}
          touchZoom={true}
        >
          <TileLayer url={TILE_URL} attribution={TILE_ATTR} tileSize={512} zoomOffset={-1} />
          {fitPositions.length > 0 && <FitBoundsController positions={fitPositions} />}

          {/* Route polylines per day */}
          {(selectedDay ? [selectedDay] : days).map(day => {
            const color = DAY_COLORS[(day - 1) % DAY_COLORS.length];
            const routeCoords = routes[day];
            if (!routeCoords) return null;
            return (
              <Polyline
                key={`route-${day}`}
                positions={routeCoords}
                pathOptions={{ color, weight: 4, opacity: 0.85 }}
              />
            );
          })}

          {/* Inter-day connectors (dashed) — only in "all days" view */}
          {!selectedDay && Object.entries(routes)
            .filter(([key]) => key.startsWith('between-'))
            .map(([key, coords]) => (
              <Polyline
                key={key}
                positions={coords}
                pathOptions={{ color: '#9CA3AF', weight: 2, opacity: 0.5, dashArray: '6,9' }}
              />
            ))}

          {/* Activity markers */}
          {activityMarkers.map((activity, idx) => {
            const color = DAY_COLORS[(activity.day_number - 1) % DAY_COLORS.length];
            const isFirst = activity.positionInDay === 1;
            return (
              <CircleMarker
                key={`marker-${activity.id || idx}`}
                center={[activity.latitude, activity.longitude]}
                radius={isFirst ? 12 : 8}
                pathOptions={{
                  fillColor: color,
                  fillOpacity: 0.9,
                  color: '#ffffff',
                  weight: isFirst ? 3 : 2,
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} permanent={false} sticky={false}>
                  <div className="text-xs font-semibold">
                    <span className="text-platinum-400 mr-1">Day {activity.day_number} ·</span>
                    {activity.title}
                    {activity.location ? <div className="text-platinum-400 font-normal">{activity.location}</div> : null}
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 z-[400] pointer-events-none">
          <div className="flex items-center justify-between p-4">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg px-4 py-2 pointer-events-auto">
              <p className="text-sm font-semibold text-charcoal-500">
                {itinerary?.destination}
                <span className="text-platinum-500 font-normal ml-2">· {itinerary?.trip_length} days</span>
                {selectedDay && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs text-white font-bold" style={{ backgroundColor: DAY_COLORS[(selectedDay - 1) % DAY_COLORS.length] }}>
                    Day {selectedDay}{dayLocationLabels[selectedDay] ? ` · ${dayLocationLabels[selectedDay]}` : ''}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 pointer-events-auto">
              <button
                onClick={openGoogleMaps}
                className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg px-3 py-2 text-sm font-medium text-charcoal-500 hover:bg-white transition-colors flex items-center gap-1.5"
                title="Open in Google Maps"
              >
                <Navigation size={14} />
                Google Maps
              </button>
              <button
                onClick={onClose}
                className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-2 text-charcoal-500 hover:bg-white transition-colors"
                title="Close map"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Day filter strip */}
      <div className="bg-white border-t border-platinum-200 px-4 py-3 flex items-center gap-2 overflow-x-auto scrollbar-hide" onClick={e => e.stopPropagation()}>
        <button
          onClick={() => setSelectedDay(null)}
          className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
            !selectedDay ? 'bg-charcoal-500 text-white shadow-md' : 'bg-platinum-100 text-charcoal-400 hover:bg-platinum-200'
          }`}
        >
          All days
        </button>
        {days.map(day => {
          const color = DAY_COLORS[(day - 1) % DAY_COLORS.length];
          const isActive = selectedDay === day;
          const label = dayLocationLabels[day];
          const hasCoords = activitiesWithCoords.some(a => a.day_number === day);
          if (!hasCoords) return null;
          return (
            <button
              key={day}
              onClick={() => handleDayFilter(day)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                isActive ? 'shadow-md text-white' : 'bg-platinum-100 text-charcoal-400 hover:bg-platinum-200'
              }`}
              style={isActive ? { backgroundColor: color } : {}}
            >
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.7)' : color }}
              />
              Day {day}{label ? ` · ${label}` : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}
